import { Helmet } from "react-helmet-async";

const SITE_NAME = "Dr. P. B. Boss";
const FULL_NAME = "Dr. P. B. Boss";
const DEFAULT_DESC =
  "Dr. P. B. Boss — entrepreneur, philanthropist and Founder & Chairman of the Osaka Group. Honorary Tourism Commissioner — India to the European Union, with three decades of leadership in global travel and tourism.";
const DEFAULT_IMAGE = "/images/hero-blue.jpg";

const origin = () =>
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "https://pbboss.com";

const abs = (path) =>
  path?.startsWith("http") ? path : `${origin()}${path || ""}`;

/**
 * Reusable per-route SEO: title, description, canonical, Open Graph,
 * Twitter card and optional JSON-LD structured data.
 */
export default function Seo({
  title,
  description = DEFAULT_DESC,
  path = "",
  image = DEFAULT_IMAGE,
  type = "profile",
  schema,
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : "Dr. P. B. Boss | Founder & Chairman, Osaka Group";
  const url = abs(path);
  const img = abs(image);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content={FULL_NAME} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />

      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}

/**
 * Person + Organization JSON-LD (@graph) describing Dr. P. B. Boss and the
 * Osaka Group, for rich results on Google / news discovery.
 */
export const buildProfileSchema = () => {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "@id": `${base}/#person`,
        name: "Dr. P. B. Boss",
        honorificPrefix: "Dr.",
        jobTitle: "Founder & Chairman",
        description:
          "Entrepreneur, philanthropist and visionary with three decades of leadership in the global travel and tourism industry. Founder & Chairman of the Osaka Group and Honorary Tourism Commissioner — India to the European Union.",
        url: base,
        image: `${base}/images/hero-blue.jpg`,
        email: "mailto:pbboss@osakaair.com",
        worksFor: { "@id": `${base}/#organization` },
        alumniOf: {
          "@type": "CollegeOrUniversity",
          name: "Sree Sankara College, Kalady, Kerala",
        },
        address: {
          "@type": "PostalAddress",
          addressLocality: "Angamaly",
          addressRegion: "Kerala",
          addressCountry: "IN",
        },
        hasOccupation: {
          "@type": "Occupation",
          name: "Honorary Tourism Commissioner — India to the European Union",
        },
        sameAs: ["https://pbboss.com"],
      },
      {
        "@type": "Organization",
        "@id": `${base}/#organization`,
        name: "Osaka Group",
        url: base,
        logo: "https://pbboss.com/wp-content/uploads/2022/03/osaka-group.jpg",
        foundingDate: "1993",
        founder: { "@id": `${base}/#person` },
        address: {
          "@type": "PostalAddress",
          addressLocality: "Angamaly",
          addressRegion: "Kerala",
          addressCountry: "IN",
        },
        areaServed: [
          "IN",
          "GB",
          "AU",
          "CA",
          "US",
          "AE",
          "SG",
        ],
        email: "pbboss@osakaair.com",
      },
    ],
  };
};
